import { Stack, ThemeProvider } from "@fluentui/react";
import React, { useEffect, useRef, useState } from "react";
import { Measures, addMarkerToPageTimings } from "../../../telemetry/Measures";
import { TelemetryData } from "../../../telemetry/TelemetryData";
import { dashboard } from "../../../telemetry/TraceLogging";
import { darkTheme, defaultAccentColorRamp, lightTheme } from "../../appTheme";
import { AnimationContext } from "../../contexts/AnimationContext";
import { ViewStateContext } from "../../contexts/ViewStateContext";
import { addAnimationSettingChangedEventListener, addSystemThemeChangedEventListener, getAccentColorRamp, getIsAnimationEnabled, getSystemTheme, removeAnimationSettingChangedEventListener, removeSystemThemeChangedEventListener } from "../../externalApi/ThemeApi";
import { addViewStateUpdatedEventListener, getViewState, removeViewStateUpdatedEventListener, requestHide } from "../../externalApi/WindowApi";
import { tryGetHostApi, } from "../../externalApi/externalApi";
import { PageManager } from "../PageManager/PageManager";
import { getContainerStyle } from "./RootContainer.styles";
const RootContainer = ({ isAugmentedEntryPointEnabled, isAvoidRenderWhileHiddenEnabled, isExpandedViewEnabled, }) => {
    addMarkerToPageTimings("MEE.RootContainerStart");
    const [theme, setTheme] = useState(lightTheme(defaultAccentColorRamp));
    const viewChangedApi = window.__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("viewchanged", 1, 1);
    useEffect(() => {
        function updateSystemTheme() {
            Promise.all([getSystemTheme(), getAccentColorRamp()]).then(([systemColors, accentColorRamp]) => {
                const systemBackgroundColor = systemColors.substr(0, 6);
                const isDarkTheme = systemBackgroundColor === "000000";
                setTheme(() => (isDarkTheme ? darkTheme(accentColorRamp) : lightTheme(accentColorRamp)));
                const root = document.documentElement;
                root === null || root === void 0 ? void 0 : root.style.setProperty("--focus-outline-color", isDarkTheme
                    ? darkTheme(accentColorRamp).customSemanticColors.focusOuter
                    : lightTheme(accentColorRamp).customSemanticColors.focusOuter);
                Measures.logEvent(dashboard, "UpdateSystemThemeEvent", 16777216 /* productAndServicePerformance */, {
                    theme: isDarkTheme ? "dark" : "light",
                });
            }, () => new Error("System theme or accent color ramp unavailable"));
        }
        updateSystemTheme();
        addSystemThemeChangedEventListener(updateSystemTheme);
        return function cleanup() {
            removeSystemThemeChangedEventListener(updateSystemTheme);
        };
    }, []);
    const [isAnimationEnabled, setIsAnimationEnabled] = useState(true);
    useEffect(() => {
        function updateIsAnimationEnabled() {
            getIsAnimationEnabled().then((result) => setIsAnimationEnabled(result));
        }
        updateIsAnimationEnabled();
        addAnimationSettingChangedEventListener(updateIsAnimationEnabled);
        return function cleanup() {
            removeAnimationSettingChangedEventListener(updateIsAnimationEnabled);
        };
    }, []);
    useEffect(() => {
        window.addEventListener("resize", onWindowResize);
        return function cleanup() {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);
    function onWindowResize() {
        tryGetHostApi("Window", 1, 1).then((windowApi) => {
            if (windowApi && windowApi.notifyResizeComplete) {
                windowApi.notifyResizeComplete();
            }
        });
    }
    // This keeps track of the view state and is passed to other components via context
    const [isFullView, setIsFullView] = useState(false);
    // This is used to prevent the resize button from being pressed while a resize is in progress
    const [isResizeButtonEnabled, setIsResizeButtonEnabled] = useState(true);
    const viewStateChangedInfo = useRef({
        newView: 0 /* Hidden */,
        oldView: 0 /* Hidden */,
    });
    function logViewStateChanged(windowViewState) {
        Measures.logEvent(dashboard, "WebAppViewStateChanged", 16777216 /* productAndServicePerformance */, {
            newView: windowViewState,
            oldView: viewStateChangedInfo.current.newView,
        });
        viewStateChangedInfo.current.oldView = viewStateChangedInfo.current.newView;
        viewStateChangedInfo.current.newView = windowViewState;
    }
    function notifyViewStateChanged(viewState) {
        logViewStateChanged(viewState);
        TelemetryData.view = (viewState === 2 /* FullView */);
        setIsFullView(viewState === 2 /* FullView */);
        if (viewChangedApi) {
            viewChangedApi.fireViewChangedEvent(viewState === 2 /* FullView */ ? "fullView" : "halfView");
        }
        setIsResizeButtonEnabled(true);
    }
    useEffect(() => {
        if (isExpandedViewEnabled) {
            onViewStateChanged();
            addViewStateUpdatedEventListener(onViewStateChanged);
            return function cleanup() {
                removeViewStateUpdatedEventListener(onViewStateChanged);
            };
        }
        else {
            TelemetryData.view = false; // halfView == false, fullView == true
        }
        return;
    }, [isExpandedViewEnabled]);
    // Retrieve the view state from the host app to initialize the state
    // Whenever the view state changes, we update the state and MainPage will update its layout to match
    function onViewStateChanged() {
        if (isExpandedViewEnabled) {
            getViewState().then((viewState) => {
                notifyViewStateChanged(viewState);
            }, () => new Error("View state unavailable"));
        }
    }
    function updateResizeButtonEnabled(isEnabled) {
        if (isExpandedViewEnabled) {
            setIsResizeButtonEnabled(isEnabled);
        }
    }
    // Non-ux-state value to store whether the click, which is possibly handled by both handleRootContainerClick and
    // handleActiveAreaClick, was a Active Area Click (IE- Not to a dimiss area.) This cannot be a useState due to the
    // async speed preventing the update from happening in time for both functions.
    let isActiveAreaClick = false;
    function handleRootContainerClick() {
        if (isExpandedViewEnabled) {
            // Only allow light dismiss in expanded view
            if (isFullView && !isActiveAreaClick) {
                requestHide();
                Measures.logEvent(dashboard, "ExpandedViewAcrylicDismiss", 33554432 /* productAndServiceUsage */);
            }
            isActiveAreaClick = false;
        }
    }
    // Clicks in active areas do not trigger light dismiss
    function handleActiveAreaClick(e) {
        isActiveAreaClick = true;
    }
    return (React.createElement(ThemeProvider, { theme: theme, style: { background: "transparent", height: "100%" } },
        React.createElement(ViewStateContext.Provider, { value: {
                isFullView: isFullView,
                isFullViewEnabled: isExpandedViewEnabled,
                isResizeButtonEnabled: isResizeButtonEnabled,
                setIsResizeButtonEnabled: (isEnabled) => updateResizeButtonEnabled(isEnabled),
            } }, isExpandedViewEnabled ? (React.createElement(AnimationContext.Provider, { value: { isAnimationEnabled: isAnimationEnabled } },
            React.createElement("div", { onClick: () => handleRootContainerClick(), style: { height: "100%" } },
                React.createElement(Stack, { id: "root-container", styles: getContainerStyle(), horizontalAlign: "center", tabIndex: -1 },
                    React.createElement(PageManager, { isAugmentedEntryPointEnabled: isAugmentedEntryPointEnabled, isAvoidRenderWhileHiddenEnabled: isAvoidRenderWhileHiddenEnabled, onClick: (e) => handleActiveAreaClick(e) }))))) : (React.createElement(AnimationContext.Provider, { value: { isAnimationEnabled: isAnimationEnabled } },
            React.createElement(Stack, { id: "root-container", styles: getContainerStyle(), horizontalAlign: "center", tabIndex: -1 },
                React.createElement(PageManager, { isAugmentedEntryPointEnabled: isAugmentedEntryPointEnabled, isAvoidRenderWhileHiddenEnabled: isAvoidRenderWhileHiddenEnabled, onClick: (e) => handleActiveAreaClick(e) })))))));
};
export default RootContainer;
